import {
  inject,
  onBeforeUnmount,
  onMounted,
  type Ref,
  ref,
  shallowRef,
  watch,
} from 'vue';

import {
  UnresolvedFile,
  type UnresolvedFileOptions,
} from '../../components/UnresolvedFile';
import type { GetHoveredLineResult } from '../../managers/InteractionManager';
import type {
  DiffLineAnnotation,
  FileContents,
  FileDiffMetadata,
  MergeConflictActionPayload,
  MergeConflictMarkerRow,
  MergeConflictResolution,
  SelectedLineRange,
} from '../../types';
import { areOptionsEqual } from '../../utils/areOptionsEqual';
import {
  type MergeConflictDiffAction,
  parseMergeConflictDiffFromFile,
} from '../../utils/parseMergeConflictDiffFromFile';
import { noopRender } from '../constants';
import { WorkerPoolInjectionKey } from '../injection';

interface UnresolvedState {
  fileDiff: FileDiffMetadata;
  actions: (MergeConflictDiffAction | undefined)[];
  markerRows: MergeConflictMarkerRow[];
}

export interface UseUnresolvedFileInstanceParams {
  props: {
    file: FileContents;
    options?: UnresolvedFileOptions<undefined>;
    lineAnnotations?: DiffLineAnnotation[];
    selectedLines?: SelectedLineRange | null;
    prerenderedHTML?: string;
    disableWorkerPool: boolean;
  };
  hasConflictUtility(): boolean;
  hasCustomHeader(): boolean;
  hasGutterRenderer(): boolean;
}

export interface UseUnresolvedFileInstanceReturn {
  containerRef: Ref<HTMLElement | null>;
  state: Ref<UnresolvedState>;
  getHoveredLine(): GetHoveredLineResult<'diff'> | undefined;
  getInstance(): UnresolvedFile | undefined;
  resolveConflict(
    conflictIndex: number,
    resolution: MergeConflictResolution
  ): void;
}

// Owns the imperative `UnresolvedFile` instance and its resolved-diff state.
// Like the React adapter, `UnresolvedFile` is intentionally uncontrolled: the
// `file` prop seeds the parsed conflict state once, and subsequent resolutions
// (from either the built-in UI or a custom `#conflict` slot) advance an internal
// source-of-truth so sequential resolutions apply to the latest contents.
export function useUnresolvedFileInstance({
  props,
  hasConflictUtility,
  hasCustomHeader,
  hasGutterRenderer,
}: UseUnresolvedFileInstanceParams): UseUnresolvedFileInstanceReturn {
  const containerRef = ref<HTMLElement | null>(null);
  const poolManager = inject(WorkerPoolInjectionKey, undefined);
  let instance: UnresolvedFile | undefined;

  const initial = parseMergeConflictDiffFromFile(
    props.file,
    props.options?.maxContextLines
  );
  const state = shallowRef<UnresolvedState>({
    fileDiff: initial.fileDiff,
    actions: initial.actions,
    markerRows: initial.markerRows,
  });

  // Applies a resolution against the current resolved-diff and advances state.
  // Shared by the built-in action UI (`onMergeConflictAction`) and the custom
  // `#conflict` slot's `resolveConflict` callback.
  function applyResolution(
    conflictIndex: number,
    resolution: MergeConflictResolution
  ): void {
    if (instance == null) {
      return;
    }
    const result = instance.resolveConflict(
      conflictIndex,
      resolution,
      state.value.fileDiff
    );
    if (result != null) {
      state.value = {
        fileDiff: result.fileDiff,
        actions: result.actions,
        markerRows: result.markerRows,
      };
    }
  }

  function handleMergeConflictAction(
    payload: MergeConflictActionPayload
  ): void {
    applyResolution(payload.conflict.conflictIndex, payload.resolution);
  }

  function buildOptions(): UnresolvedFileOptions<undefined> {
    return mergeUnresolvedOptions({
      options: props.options,
      controlledSelection: props.selectedLines !== undefined,
      hasConflictUtility: hasConflictUtility(),
      hasCustomHeader: hasCustomHeader(),
      hasGutterRenderer: hasGutterRenderer(),
      onMergeConflictAction: handleMergeConflictAction,
    });
  }

  onMounted(() => {
    const fileContainer = containerRef.value;
    if (fileContainer == null) {
      throw new Error(
        'useUnresolvedFileInstance: container element missing on mount'
      );
    }
    instance = new UnresolvedFile(
      buildOptions(),
      props.disableWorkerPool ? undefined : poolManager,
      true
    );
    void instance.hydrate({
      fileDiff: state.value.fileDiff,
      actions: state.value.actions,
      markerRows: state.value.markerRows,
      fileContainer,
      lineAnnotations: props.lineAnnotations,
      prerenderedHTML: props.prerenderedHTML,
    });
  });

  watch(
    () =>
      [
        state.value,
        props.options,
        props.lineAnnotations,
        props.selectedLines,
        hasConflictUtility(),
        hasCustomHeader(),
        hasGutterRenderer(),
      ] as const,
    () => {
      if (instance == null) {
        return;
      }
      const nextOptions = buildOptions();
      const forceRender = !areOptionsEqual(instance.options, nextOptions);
      instance.setOptions(nextOptions);
      void instance.render({
        fileDiff: state.value.fileDiff,
        actions: state.value.actions,
        markerRows: state.value.markerRows,
        lineAnnotations: props.lineAnnotations,
        forceRender,
      });
      if (props.selectedLines !== undefined) {
        instance.setSelectedLines(props.selectedLines);
      }
    },
    { flush: 'post' }
  );

  onBeforeUnmount(() => {
    instance?.cleanUp();
    instance = undefined;
  });

  return {
    containerRef,
    state,
    getHoveredLine: () => instance?.getHoveredLine(),
    getInstance: () => instance,
    resolveConflict: applyResolution,
  };
}

interface MergeUnresolvedOptionsParams {
  options: UnresolvedFileOptions<undefined> | undefined;
  controlledSelection: boolean;
  hasConflictUtility: boolean;
  hasCustomHeader: boolean;
  hasGutterRenderer: boolean;
  onMergeConflictAction: UnresolvedFileOptions<undefined>['onMergeConflictAction'];
}

function mergeUnresolvedOptions({
  options,
  controlledSelection,
  hasConflictUtility,
  hasCustomHeader,
  hasGutterRenderer,
  onMergeConflictAction,
}: MergeUnresolvedOptionsParams): UnresolvedFileOptions<undefined> {
  return {
    ...options,
    controlledSelection,
    onMergeConflictAction,
    // A `#conflict` slot drives custom action UI, so hand the core a placeholder
    // that allocates the merge-conflict slots without rendering built-in UI.
    mergeConflictActionsType: hasConflictUtility
      ? noopRender
      : options?.mergeConflictActionsType,
    renderCustomHeader: hasCustomHeader ? noopRender : undefined,
    renderGutterUtility: hasGutterRenderer ? noopRender : undefined,
  };
}
